import React, { useMemo, useEffect } from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { I18nextProvider, useTranslation } from 'react-i18next';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import rtlPlugin from 'stylis-plugin-rtl';
import { prefixer } from 'stylis';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { AuthProvider, useAuth } from './context/AuthContext';
import { BranchProvider } from './BranchContext';
import adminI18n from './i18n';

// Layout
import Layout from './components/Layout';

// Pages
import Dashboard from './pages/Dashboard';
import Sales from './pages/Sales';
import BranchSales from './pages/BranchSales';
import Branches from './pages/Branches';
import AcceptingStores from './pages/AcceptingStores';
import AddBranch from './pages/AddBranch';
import EditBranch from './pages/EditBranch';
import Categories from './pages/Categories';
import AddCategory from './pages/AddCategory';
import Cities from './pages/Cities';
import Drivers from './pages/Drivers';
import RegisterDriver from './pages/RegisterDriver';
import Offers from './pages/Offers';
import Orders from './pages/Orders';
import OrderHistory from './pages/OrderHistory';
import Products from './pages/Products';
import AddProduct from './pages/AddProduct';
import EditProduct from './pages/EditProduct';
import AddCustomProduct from './pages/AddCustomProduct';
import GroupedProducts from './pages/GroupedProducts';
import Restaurant from './pages/Restaurant';
import RestaurantSlider from './pages/RestaurantSlider';
import Users from './pages/Users';
import Admins from './pages/Admins';
import ResetPassword from './pages/ResetPassword';
import RegisterAdmin from './pages/RegisterAdmin';
import Notifications from './pages/Notifications';
import AddNotification from './pages/AddNotification';
import EditNotification from './pages/EditNotification';
import Feedback from './pages/Feedback';
import UserFeedbackDetails from './pages/UserFeedbackDetails';
import Filters from './pages/Filters';
import QRCodes from './pages/QRCodes';
import GenerateQRCodes from './pages/GenerateQRCodes';
import AssignQRCode from './pages/AssignQRCode';
import QRCodeStats from './pages/QRCodeStats';
import EditQRCode from './pages/EditQRCode';
import Wallets from './pages/Wallets';
import AddWallet from './pages/AddWallet';
import EditWallet from './pages/EditWallet';
import BranchWallets from './pages/BranchWallets';
import Transactions from './pages/Transactions';
import TransactionHistory from './pages/TransactionHistory';
import BackupManagement from './pages/BackupManagement';
import RentalManagement from './pages/RentalManagement';
import Coupons from './pages/Coupons';
import AddEditCoupon from './pages/AddEditCoupon';
import CouponStats from './pages/CouponStats';

// ✅ NEW: Design System Imports
import DesignPlans from './pages/DesignPlans';
import AddEditDesignPlan from './pages/AddEditDesignPlan';
import DesignSubscriptions from './pages/DesignSubscriptions';
import DesignTransactionHistory from './pages/DesignTransactionHistory';
import DesignManagement from './pages/DesignManagement';
import DesignSubscriptionDetail from './pages/DesignSubscriptionDetail';
import PurchaseDesignPlan from './pages/PurchaseDesignPlan';
import MyDesignSubscriptions from './pages/MyDesignSubscriptions';

// Protected Route Component
const ProtectedRoute = () => {
  const { user, loading } = useAuth();
  
  if (loading) return null;
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return <Outlet />;
};

// Permission Route Component
const PermissionRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) return null;
  
  if (!user || user.Permission !== true) {
    return <Navigate to="/admin/sales/branch" replace />;
  }
  
  return children;
};

// Root Redirect Component
const RootRedirect = () => {
  const { user } = useAuth();
  const hasPermission = user && user.Permission === true;
  
  if (hasPermission) {
    return <Dashboard />;
  } else {
    return <Navigate to="/admin/sales/branch" replace />;
  }
};

function AdminAppContent() {
  const { i18n } = useTranslation();
  const isRtl = i18n.dir() === 'rtl';

  useEffect(() => {
    document.dir = isRtl ? 'rtl' : 'ltr';
    document.documentElement.setAttribute('lang', i18n.language);
  }, [isRtl, i18n.language]);

  const cache = useMemo(() => {
    return createCache({
      key: isRtl ? 'muirtl-admin' : 'muiltr-admin',
      stylisPlugins: isRtl ? [prefixer, rtlPlugin] : [prefixer],
    });
  }, [isRtl]);

  const theme = useMemo(() => createTheme({
    direction: isRtl ? 'rtl' : 'ltr',
    typography: {
      fontFamily: isRtl ? 'Cairo, Tajawal, sans-serif' : 'Inter, Roboto, sans-serif',
    },
    palette: {
      primary: {
        main: '#1976d2',
        light: '#42a5f5',
        dark: '#1565c0',
      },
      secondary: {
        main: '#9c27b0',
        light: '#ba68c8',
        dark: '#7b1fa2',
      },
      error: {
        main: '#d32f2f',
      },
      success: {
        main: '#2e7d32',
      },
      warning: {
        main: '#ed6c02',
      },
      background: {
        default: '#f5f7fa',
        paper: '#ffffff',
      },
    },
    shape: {
      borderRadius: 8,
    },
  }), [isRtl]);

  return (
    <CacheProvider value={cache}>
      <ThemeProvider theme={theme}>
        <AuthProvider>
          <BranchProvider>
            <Routes>
              <Route element={<ProtectedRoute />}>
                <Route path="/" element={<Layout />}>
                  <Route index element={<RootRedirect />} />
                  
                  {/* Dashboard */}
                  <Route path="dashboard" element={
                    <PermissionRoute>
                      <Dashboard />
                    </PermissionRoute>
                  } />
                  
                  {/* Sales */}
                  <Route path="sales" element={<PermissionRoute><Sales /></PermissionRoute>} />
                  <Route path="sales/branch" element={<BranchSales />} />
                  
                  {/* Wallets */}
                  <Route path="wallets" element={<PermissionRoute><Wallets /></PermissionRoute>} />
                  <Route path="wallets/add" element={<PermissionRoute><AddWallet /></PermissionRoute>} />
                  <Route path="wallets/edit/:id" element={<PermissionRoute><EditWallet /></PermissionRoute>} />
                  <Route path="wallets/branch/:id" element={<PermissionRoute><BranchWallets /></PermissionRoute>} />

                  {/* Coupons & Vouchers */}
                  <Route path="coupons" element={<PermissionRoute><Coupons /></PermissionRoute>} />
                  <Route path="coupons/add" element={<PermissionRoute><AddEditCoupon /></PermissionRoute>} />
                  <Route path="coupons/edit/:id" element={<PermissionRoute><AddEditCoupon /></PermissionRoute>} />
                  <Route path="coupons/stats/:id" element={<PermissionRoute><CouponStats /></PermissionRoute>} />
                  
                  {/* Transactions */}
                  <Route path="transactions" element={<Transactions />} />
                  <Route path="transactions/history" element={<TransactionHistory />} />
                  
                  {/* Branches */}
                  <Route path="branches" element={<PermissionRoute><Branches /></PermissionRoute>} />
                  <Route path="branches/add" element={<PermissionRoute><AddBranch /></PermissionRoute>} />
                  <Route path="branches/edit/:id" element={<PermissionRoute><EditBranch /></PermissionRoute>} />
                  
                  {/* Accepting Stores */}
                  <Route path="accepting-stores" element={
                    <PermissionRoute>
                      <AcceptingStores />
                    </PermissionRoute>
                  } />
                  
                  {/* Backup Management */}
                  <Route path="backups" element={
                    <PermissionRoute>
                      <BackupManagement />
                    </PermissionRoute>
                  } />
                  
                  {/* Categories */}
                  <Route path="categories" element={<Categories />} />
                  <Route path="categories/add" element={<AddCategory />} />
                  
                  {/* Cities */}
                  <Route path="cities" element={<PermissionRoute><Cities /></PermissionRoute>} />
                  
                  {/* Drivers */}
                  <Route path="drivers" element={<Drivers />} />
                  <Route path="drivers/register" element={<RegisterDriver />} />
                  
                  {/* Orders */}
                  <Route path="orders" element={<Orders />} />
                  <Route path="orders/history" element={<OrderHistory />} />
                  
                  {/* Products */}
                  <Route path="products" element={<Products />} />
                  <Route path="products/add" element={<AddProduct />} />
                  <Route path="products/add-custom" element={<AddCustomProduct />} />
                  <Route path="products/edit/:id" element={<EditProduct />} />
                  <Route path="products/grouped" element={<GroupedProducts />} />
                  <Route path="offers" element={<Offers />} />
                  <Route path="rentals" element={<RentalManagement />} />
                  
                  {/* Restaurant */}
                  <Route path="restaurant" element={<PermissionRoute><Restaurant /></PermissionRoute>} />
                  <Route path="restaurant/slider" element={<PermissionRoute><RestaurantSlider /></PermissionRoute>} />
                  
                  {/* Users & Admins */}
                  <Route path="users" element={<PermissionRoute><Users /></PermissionRoute>} />
                  <Route path="admins" element={<Admins />} />
                  <Route path="admins/reset-password" element={<ResetPassword />} />
                  <Route path="admins/register" element={<PermissionRoute><RegisterAdmin /></PermissionRoute>} />
                  
                  {/* QR Codes */}
                  <Route path="qrcodes" element={<PermissionRoute><QRCodes /></PermissionRoute>} />
                  <Route path="qrcodes/generate" element={<PermissionRoute><GenerateQRCodes /></PermissionRoute>} />
                  <Route path="qrcodes/assign/:id" element={<PermissionRoute><AssignQRCode /></PermissionRoute>} />
                  <Route path="qrcodes/stats/:id" element={<PermissionRoute><QRCodeStats /></PermissionRoute>} />
                  <Route path="qrcodes/edit/:id" element={<PermissionRoute><EditQRCode /></PermissionRoute>} />
                  
                  {/* Notifications */}
                  <Route path="notifications" element={<PermissionRoute><Notifications /></PermissionRoute>} />
                  <Route path="notifications/add" element={<AddNotification />} />
                  <Route path="notifications/edit/:id" element={<PermissionRoute><EditNotification /></PermissionRoute>} />
                  
                  {/* Feedback */}
                  <Route path="feedback" element={<Feedback />} />
                  <Route path="feedback/:userId" element={<UserFeedbackDetails />} />
                  
                  {/* Filters */}
                  <Route path="filters" element={<PermissionRoute><Filters /></PermissionRoute>} />

                  {/* ✅ NEW: Design System Routes - Permission Required (Super Admin) */}
                  <Route path="design-plans" element={
                    <PermissionRoute>
                      <DesignPlans />
                    </PermissionRoute>
                  } />
                  <Route path="design-plans/add" element={
                    <PermissionRoute>
                      <AddEditDesignPlan />
                    </PermissionRoute>
                  } />
                  <Route path="design-plans/edit/:id" element={
                    <PermissionRoute>
                      <AddEditDesignPlan />
                    </PermissionRoute>
                  } />
                  <Route path="design-requests" element={
                    <PermissionRoute>
                      <DesignSubscriptions />
                    </PermissionRoute>
                  } />
                  <Route path="design-transactions" element={
                    <PermissionRoute>
                      <DesignTransactionHistory />
                    </PermissionRoute>
                  } />
                  <Route path="design-management" element={
                    <PermissionRoute>
                      <DesignManagement />
                    </PermissionRoute>
                  } />
                  <Route path="design-management/:id" element={
                    <PermissionRoute>
                      <DesignSubscriptionDetail />
                    </PermissionRoute>
                  } />

                  {/* ✅ NEW: Design System Routes - For All Admins (No Permission Required) */}
                  <Route path="purchase-design" element={<PurchaseDesignPlan />} />
                  <Route path="my-design-subscriptions" element={<MyDesignSubscriptions />} />
                </Route>
              </Route>
            </Routes>
          </BranchProvider>
        </AuthProvider>
      </ThemeProvider>
    </CacheProvider>
  );
}

function App() {
  return (
    <I18nextProvider i18n={adminI18n}>
      <AdminAppContent />
    </I18nextProvider>
  );
}

export default App;